import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import PlayerName from "@/components/PlayerName";
import { toast } from "sonner";
import { Handshake, Loader2 } from "lucide-react";

export const PartnerBoard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/player/partner-board"); setData(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  if (!data || (data.boards.length === 0 && data.incoming.length === 0)) return null;

  const request = async (b, p) => {
    setBusy(`req-${p.user_id}`);
    try {
      await api.post(`/tournaments/${b.tournament_id}/pair-request`, { to_user_id: p.user_id, event_type: b.event_type });
      toast.success(`Partner request sent to ${p.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const respond = async (r, accept) => {
    setBusy(`resp-${r.id}`);
    try {
      const { data: res } = await api.post(`/pair-requests/${r.id}/respond`, { accept });
      if (accept) {
        toast.success(`You're paired with ${res.partner_name}! Opening your partner chat…`);
        window.dispatchEvent(new CustomEvent("pb:open-messages", { detail: { id: res.partner_id || r.from_user_id, name: r.from_name } }));
      } else {
        toast.success("Request declined");
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const pendingTo = new Set(data.outgoing.map((o) => `${o.tournament_id}|${o.event_type}|${o.to_user_id}`));

  return (
    <div className="bg-pb-surface border border-pb-cyan/40 p-5 mb-6" data-testid="partner-board">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-cyan font-bold mb-1">
        <Handshake className="w-3.5 h-3.5" /> Find a Partner
      </div>
      <p className="text-xs text-zinc-500 mb-4">You're registered solo in a doubles event — pair up with another solo player below.</p>

      {data.incoming.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="pair-incoming">
          {data.incoming.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border border-[#A3E635]/50 bg-[#A3E635]/5 px-3 py-2.5" data-testid={`pair-incoming-${r.id}`}>
              <span className="text-sm flex-1 min-w-0">
                <span className="font-bold text-white">{r.from_name}</span>
                <span className="text-zinc-400"> wants to be your partner — {r.event_type} · {r.tournament_name}</span>
              </span>
              <button data-testid={`pair-accept-${r.id}`} disabled={busy === `resp-${r.id}`} onClick={() => respond(r, true)}
                className="bg-[#A3E635] text-black text-[10px] font-black uppercase tracking-widest px-3 py-1.5 hover:bg-white active:scale-95 transition-all disabled:opacity-50">
                {busy === `resp-${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Accept"}
              </button>
              <button data-testid={`pair-decline-${r.id}`} disabled={busy === `resp-${r.id}`} onClick={() => respond(r, false)}
                className="border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 hover:text-red-400 hover:border-red-400/60 transition-colors disabled:opacity-50">
                Decline
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {data.boards.map((b) => (
          <div key={`${b.tournament_id}-${b.event_type}`} data-testid={`pair-board-${b.tournament_id}`}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">
              {b.tournament_name} · <span className="text-pb-cyan">{b.event_type}</span> · {b.date}
            </div>
            {b.players.length === 0 ? (
              <p className="text-xs text-zinc-600">No other solo players in this event yet — check back as more register.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {b.players.map((p) => {
                  const sent = pendingTo.has(`${b.tournament_id}|${b.event_type}|${p.user_id}`);
                  return (
                    <div key={p.user_id} className="flex items-center gap-2.5 border border-pb-border px-3 py-2" data-testid={`pair-player-${p.user_id}`}>
                      <Avatar src={p.avatar} name={p.name} size={30} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold truncate"><PlayerName userId={p.user_id} name={p.name} /></span>
                        {p.division && <span className="block text-[10px] uppercase tracking-widest text-zinc-500">{p.division}</span>}
                      </span>
                      <button data-testid={`pair-request-${p.user_id}`} disabled={sent || busy === `req-${p.user_id}`}
                        onClick={() => request(b, p)}
                        className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1.5 transition-all active:scale-95 ${
                          sent ? "border border-pb-border text-zinc-600" : "bg-pb-cyan text-black hover:bg-white"} disabled:opacity-60`}>
                        {sent ? "Requested" : busy === `req-${p.user_id}` ? "…" : "Pair Up"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PartnerBoard;
